/**
 * Route-local SignalR + REST polling hook for Edge Live Monitor.
 *
 * Per spec §7 + §14 Q-3, this is **route-local** — it does NOT modify
 * SBL500-005's `useLiveMachineStatus` hook. The two hooks coexist with
 * different lifetimes and call shapes.
 *
 * Behaviour:
 *   1. On mount, immediately fetches initial status + last 50 events via REST.
 *   2. Connects to the gateway SignalR hub at
 *      `${machineGatewayApiBaseUrl}/hubs/gateway` and invokes
 *      `JoinMachine(machineId)`. The hub silently no-ops on permission failure
 *      — the client falls back to polling.
 *   3. Subscribes to `MachineStatusUpdated` (replaces full status in place) and
 *      `MachineEventAppended` (prepends to event stream, capped at 50).
 *   4. After 3 consecutive failed reconnect attempts the hook drops to 5s REST
 *      polling for both /status and /events?limit=50.
 *   5. Tracks observed polling rate (signalR arrivals / 60s rolling window) and
 *      error count (failed REST polls + SignalR reconnect attempts +
 *      JoinMachine failures / 5-min rolling window) for Zone 4.
 *
 * `signals` field on MachineStatusDto is forward-declared for §13.2 — until
 * backend deploys, it will be undefined and Zone 2 renders only the 11
 * normalized fields plus stale tiles for the rest.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IRetryPolicy,
  LogLevel,
  RetryContext,
} from "@microsoft/signalr";
import accountContext from "../../../account/accountContext";
import edgeMonitorApi from "../edgeMonitorApi";
import {
  MachineEventDto,
  MachineStatusDto,
  SignalRConnState,
} from "../models/edgeMonitorModels";

const EVENT_HISTORY_LIMIT = 50;
const POLLING_INTERVAL_MS = 5000;
const RECONNECT_FAILURES_BEFORE_POLLING = 3;
const POLLING_RATE_WINDOW_MS = 60_000; // 60s rolling window
const ERROR_WINDOW_MS = 5 * 60_000; // 5-min rolling window

class ExponentialRetryPolicy implements IRetryPolicy {
  private static readonly MAX_DELAY_MS = 5_000; // cap at 5s — internal staff debug

  nextRetryDelayInMilliseconds(retryContext: RetryContext): number | null {
    if (retryContext.previousRetryCount === 0) return 0;
    return Math.min(
      1000 * Math.pow(2, retryContext.previousRetryCount),
      ExponentialRetryPolicy.MAX_DELAY_MS
    );
  }
}

export interface UseEdgeMonitorSignalRResult {
  /** Most recent status snapshot. null = not yet loaded or no row. */
  status: MachineStatusDto | null;
  /** Last 50 events, newest first. */
  events: MachineEventDto[];
  /** Pill state in Zone 1. */
  connState: SignalRConnState;
  /** Polling-rate observed: events/min over rolling 60s. */
  pollingRatePerMin: number;
  /** Errors-in-5-min: rolling 5m count. */
  errorsLast5Min: number;
  /** ISO timestamp of the most recent SignalR push (for stale logic). */
  lastPushAt: string | null;
  /** ISO timestamp of the most recent ANY data arrival (push OR poll). */
  lastDataAt: string | null;
  /** Did the latest GET /status return null (machine not found / no access)? */
  notFoundOrNoAccess: boolean;
}

export const useEdgeMonitorSignalR = (
  machineId: string | null
): UseEdgeMonitorSignalRResult => {
  const [status, setStatus] = useState<MachineStatusDto | null>(null);
  const [events, setEvents] = useState<MachineEventDto[]>([]);
  const [connState, setConnState] = useState<SignalRConnState>("offline");
  const [pollingRatePerMin, setPollingRatePerMin] = useState(0);
  const [errorsLast5Min, setErrorsLast5Min] = useState(0);
  const [lastPushAt, setLastPushAt] = useState<string | null>(null);
  const [lastDataAt, setLastDataAt] = useState<string | null>(null);
  const [notFoundOrNoAccess, setNotFoundOrNoAccess] = useState(false);

  // Refs for the rolling-window data structures.
  // Each is a list of timestamps (ms since epoch). On every read/write we
  // shift off entries older than the window and recompute the count.
  const arrivalTimestampsRef = useRef<number[]>([]);
  const errorTimestampsRef = useRef<number[]>([]);

  const connectionRef = useRef<HubConnection | null>(null);
  const pollingTimerRef = useRef<number | null>(null);
  const tickTimerRef = useRef<number | null>(null);

  // ---------- counter helpers ----------
  const recordArrival = useCallback(() => {
    const now = Date.now();
    arrivalTimestampsRef.current.push(now);
    // Trim then update state.
    const cutoff = now - POLLING_RATE_WINDOW_MS;
    arrivalTimestampsRef.current = arrivalTimestampsRef.current.filter(
      (t) => t >= cutoff
    );
    setPollingRatePerMin(arrivalTimestampsRef.current.length);
    setLastDataAt(new Date(now).toISOString());
  }, []);

  const recordError = useCallback(() => {
    const now = Date.now();
    errorTimestampsRef.current.push(now);
    const cutoff = now - ERROR_WINDOW_MS;
    errorTimestampsRef.current = errorTimestampsRef.current.filter(
      (t) => t >= cutoff
    );
    setErrorsLast5Min(errorTimestampsRef.current.length);
  }, []);

  // 1Hz tick to (a) trim rolling windows so counters decrement when no new
  // arrivals/errors come in, and (b) keep "last data N s ago" relative
  // timestamps fresh in Zone 1.
  useEffect(() => {
    tickTimerRef.current = window.setInterval(() => {
      const now = Date.now();
      const arrCutoff = now - POLLING_RATE_WINDOW_MS;
      const errCutoff = now - ERROR_WINDOW_MS;
      const newArr = arrivalTimestampsRef.current.filter((t) => t >= arrCutoff);
      const newErr = errorTimestampsRef.current.filter((t) => t >= errCutoff);
      if (newArr.length !== arrivalTimestampsRef.current.length) {
        arrivalTimestampsRef.current = newArr;
        setPollingRatePerMin(newArr.length);
      }
      if (newErr.length !== errorTimestampsRef.current.length) {
        errorTimestampsRef.current = newErr;
        setErrorsLast5Min(newErr.length);
      }
    }, 1000);
    return () => {
      if (tickTimerRef.current !== null) {
        window.clearInterval(tickTimerRef.current);
        tickTimerRef.current = null;
      }
    };
  }, []);

  // ---------- REST helpers ----------
  const refreshStatus = useCallback(async () => {
    if (!machineId) return;
    try {
      const s = await edgeMonitorApi.getStatus(machineId);
      if (s == null) {
        // The api wrapper collapses 403/null into null. Mark not-found-or-no-access
        // when we have no events either; otherwise keep a previous snapshot if any.
        setNotFoundOrNoAccess(true);
        return;
      }
      setNotFoundOrNoAccess(false);
      setStatus(s);
      recordArrival();
    } catch {
      recordError();
    }
  }, [machineId, recordArrival, recordError]);

  const refreshEvents = useCallback(async () => {
    if (!machineId) return;
    try {
      const e = await edgeMonitorApi.getEvents(machineId, EVENT_HISTORY_LIMIT);
      setEvents(e);
    } catch {
      recordError();
    }
  }, [machineId, recordError]);

  // ---------- polling fallback ----------
  const startPolling = useCallback(() => {
    if (pollingTimerRef.current !== null) return;
    setConnState("polling");
    const tick = () => {
      void refreshStatus();
      void refreshEvents();
    };
    pollingTimerRef.current = window.setInterval(tick, POLLING_INTERVAL_MS);
  }, [refreshStatus, refreshEvents]);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current !== null) {
      window.clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  // ---------- main effect — initial paint + SignalR ----------
  useEffect(() => {
    // Reset on machineId change.
    setStatus(null);
    setEvents([]);
    setConnState("offline");
    setNotFoundOrNoAccess(false);
    arrivalTimestampsRef.current = [];
    errorTimestampsRef.current = [];
    setPollingRatePerMin(0);
    setErrorsLast5Min(0);
    setLastPushAt(null);
    setLastDataAt(null);

    if (!machineId) {
      // Empty / missing query param — Zones 2..4 suppress, no fetch.
      return;
    }

    // 1) Initial paint via REST. Always fires once regardless of SignalR state.
    void refreshStatus();
    void refreshEvents();

    // 2) Try SignalR.
    const baseUrl = (window as any).app?.config?.machineGatewayApiBaseUrl;
    if (!baseUrl) {
      // No gateway URL configured — fall straight to polling.
      startPolling();
      return () => {
        stopPolling();
      };
    }

    let consecutiveReconnectFailures = 0;
    let cancelled = false;

    const connection = new HubConnectionBuilder()
      .withUrl(`${baseUrl}/hubs/gateway`, {
        accessTokenFactory: () => accountContext.accessToken || "",
      })
      .withAutomaticReconnect(new ExponentialRetryPolicy())
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on("MachineStatusUpdated", (payload: MachineStatusDto) => {
      if (cancelled) return;
      setStatus(payload);
      setLastPushAt(new Date().toISOString());
      recordArrival();
    });

    connection.on("MachineEventAppended", (payload: MachineEventDto) => {
      if (cancelled) return;
      setEvents((prev) => {
        const next = [payload, ...prev];
        return next.length > EVENT_HISTORY_LIMIT
          ? next.slice(0, EVENT_HISTORY_LIMIT)
          : next;
      });
      setLastPushAt(new Date().toISOString());
      recordArrival();
    });

    connection.onreconnecting(() => {
      if (cancelled) return;
      setConnState("reconnecting");
      recordError();
      consecutiveReconnectFailures += 1;
      if (consecutiveReconnectFailures >= RECONNECT_FAILURES_BEFORE_POLLING) {
        startPolling();
      }
    });

    connection.onreconnected(() => {
      if (cancelled) return;
      consecutiveReconnectFailures = 0;
      stopPolling();
      setConnState("live");
      // Re-join the group on reconnect.
      void connection.invoke("JoinMachine", machineId).catch(() => {
        recordError();
      });
    });

    connection.onclose(() => {
      if (cancelled) return;
      setConnState("offline");
      startPolling();
    });

    connection
      .start()
      .then(() => {
        if (cancelled) return;
        setConnState("live");
        return connection.invoke("JoinMachine", machineId);
      })
      .catch(() => {
        if (cancelled) return;
        recordError();
        startPolling();
      });

    connectionRef.current = connection;

    return () => {
      cancelled = true;
      stopPolling();
      if (
        connectionRef.current &&
        connectionRef.current.state !== HubConnectionState.Disconnected
      ) {
        connectionRef.current
          .invoke("LeaveMachine", machineId)
          .catch(() => undefined)
          .finally(() => {
            connectionRef.current?.stop().catch(() => undefined);
          });
      }
      connectionRef.current = null;
    };
  }, [
    machineId,
    refreshStatus,
    refreshEvents,
    recordArrival,
    recordError,
    startPolling,
    stopPolling,
  ]);

  return {
    status,
    events,
    connState,
    pollingRatePerMin,
    errorsLast5Min,
    lastPushAt,
    lastDataAt,
    notFoundOrNoAccess,
  };
};

export default useEdgeMonitorSignalR;
